import React from 'react';
import { ExperimentOutlined } from '@ant-design/icons';
import type { NavigateFunction } from 'react-router-dom';
import { DropdownMenu, Icon } from '@hydrodigital/design-system';
import InflowSvg from '@/assets/svg/InflowSvg';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';
import type { Module } from '@/domain/Users/Users';
import type { AppDispatch } from '@/redux/store';
import { restartResumeAndReadingList } from '@/redux/slices/readingsResumesSlice';
import { publishDashboardMapEvent } from '@/events/dashboardMapEvents';
import type { MapModeId } from '@/events/dashboardMapEvents';
import { FUTURE_FEATURES } from '../constants';
import MapIcon, { ChevronDownIcon, DotsGridIcon } from '../Icons';

const MODE_ICONS: Record<MapModeId, React.ReactNode> = {
	default: <Icon name="map" />,
	'digital-twin': <Icon name="virtual-twin" />,
	dma: <Icon name="file-graph" />,
	health: <DotsGridIcon />,
	inflow: <Icon name="waves1" />,
	mms: <Icon name="code-branch" />,
	simulations: <DotsGridIcon />,
	heatmap: <ExperimentOutlined style={{ fontSize: 16 }} rev={undefined} />,
	'past-overflow': <Icon name="history" />,
	'sensor-maintenance': <Icon name="processor" />,
	alerts: <Icon name="exclamation-triangle" />,
	'sewer-analysis': <Icon name="chart-pie-alt" />,
};

interface MapModeMenuProps {
	selectedMode: MapModeId;
	setSelectedMode: (mode: MapModeId) => void;
	selectedModeLabel: string;
	isSmallScreen: boolean;
	modules: Module[];
	contextValue: DashboardContextValueInterface;
	setContextValue: React.Dispatch<React.SetStateAction<DashboardContextValueInterface>>;
	navigate: NavigateFunction;
	dispatch: AppDispatch;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	validateInflowMode: () => boolean;
	onInvalidInflow: () => void;
	openModalKeplerAnalysisHistorial: () => void;
	isWaterNetwork: boolean;
	isDrainageNetwork: boolean;
	analysisModeLabel: string;
}

const MapModeMenu: React.FC<MapModeMenuProps> = ({
	selectedMode,
	setSelectedMode,
	selectedModeLabel,
	isSmallScreen,
	modules,
	contextValue,
	setContextValue,
	navigate,
	dispatch,
	open,
	onOpenChange,
	validateInflowMode,
	onInvalidInflow,
	openModalKeplerAnalysisHistorial,
	isWaterNetwork,
	isDrainageNetwork,
	analysisModeLabel,
}) => {
	const closeAndPublishMode = (mode: MapModeId) => {
		onOpenChange(false);
		publishDashboardMapEvent({ type: 'setMode', data: mode });
	};

	const selectDefaultMode = () => {
		setSelectedMode('default');
		navigate('/');
		dispatch(restartResumeAndReadingList());
		publishDashboardMapEvent({ type: 'closePopup' });
		setContextValue((prev) => ({
			...prev,
			selectedSensors: [],
			activatedMmsMode: false,
			activatedDigitalTwinMode: false,
			activatedInflowMode: false,
			activatedHeatmapMode: false,
			activatedPastOverflowMode: false,
			activatedSensorMaintenanceMode: false,
			activatedAlertMode: false,
			activatedSewerAnalysisMode: false,
		}));
		closeAndPublishMode('default');
	};

	const toggleDigitalTwinMode = () => {
		const newValue = !contextValue.activatedDigitalTwinMode;
		setSelectedMode(newValue ? 'digital-twin' : 'default');

		if (!newValue) {
			publishDashboardMapEvent({ type: 'closePopup' });
		}

		setContextValue((prevContextValue) => ({
			...prevContextValue,
			activatedDigitalTwinMode: newValue,
			activatedInflowMode: false,
			activatedPastOverflowMode: false,
			activatedSensorMaintenanceMode: false,
			activatedAlertMode: false,
			activatedSewerAnalysisMode: false,
		}));
		closeAndPublishMode(newValue ? 'digital-twin' : 'default');
	};

	const selectDmaMode = () => {
		setSelectedMode('dma');
		publishDashboardMapEvent({ type: 'setMode', data: 'dma' });
		onOpenChange(false);
	};

	const toggleInflowMode = () => {
		const currentValue = contextValue.activatedInflowMode;

		if (!currentValue && !validateInflowMode()) {
			onInvalidInflow();
			return;
		}

		const newValue = !currentValue;
		setSelectedMode(newValue ? 'inflow' : 'default');
		setContextValue((prevContextValue) => ({
			...prevContextValue,
			activatedMmsMode: false,
			activatedInflowMode: newValue,
			activatedDigitalTwinMode: false,
			activatedPastOverflowMode: false,
			activatedSensorMaintenanceMode: false,
			activatedAlertMode: false,
			activatedSewerAnalysisMode: false,
		}));
		closeAndPublishMode(newValue ? 'inflow' : 'default');
	};

	const togglePastOverflowMode = () => {
		const newValue = !contextValue.activatedPastOverflowMode;
		setSelectedMode(newValue ? 'past-overflow' : 'default');

		if (!newValue) {
			publishDashboardMapEvent({ type: 'closePopup' });
		}

		setContextValue((prevContextValue) => ({
			...prevContextValue,
			activatedPastOverflowMode: newValue,
			activatedMmsMode: false,
			activatedDigitalTwinMode: false,
			activatedInflowMode: false,
			activatedSensorMaintenanceMode: false,
			activatedAlertMode: false,
			activatedSewerAnalysisMode: false,
		}));
		closeAndPublishMode(newValue ? 'past-overflow' : 'default');
	};

	const toggleHeatmapMode = () => {
		const newValue = !contextValue.activatedHeatmapMode;
		setSelectedMode(newValue ? 'heatmap' : 'default');
		setContextValue((prev) => ({
			...prev,
			activatedHeatmapMode: newValue,
			activatedDigitalTwinMode: false,
			activatedInflowMode: false,
			activatedMmsMode: false,
			activatedSensorMaintenanceMode: false,
			activatedAlertMode: false,
			activatedSewerAnalysisMode: false,
		}));
		closeAndPublishMode(newValue ? 'heatmap' : 'default');
	};

	const toggleMmsMode = () => {
		const newValue = !contextValue.activatedMmsMode;
		setSelectedMode(newValue ? 'mms' : 'default');

		if (!newValue) {
			publishDashboardMapEvent({ type: 'closePopup' });
		}

		setContextValue((prevContextValue) => ({
			...prevContextValue,
			activatedMmsMode: newValue,
			activatedDigitalTwinMode: newValue ? false : prevContextValue.activatedDigitalTwinMode,
			activatedInflowMode: newValue ? false : prevContextValue.activatedInflowMode,
			activatedPastOverflowMode: newValue ? false : prevContextValue.activatedPastOverflowMode,
			activatedSensorMaintenanceMode: false,
			activatedAlertMode: false,
			activatedSewerAnalysisMode: newValue ? false : prevContextValue.activatedSewerAnalysisMode,
		}));
		closeAndPublishMode(newValue ? 'mms' : 'default');
	};

	const toggleSensorMaintenanceMode = () => {
		const newValue = !contextValue.activatedSensorMaintenanceMode;
		setSelectedMode(newValue ? 'sensor-maintenance' : 'default');

		if (!newValue) {
			publishDashboardMapEvent({ type: 'closePopup' });
		}

		setContextValue((prevContextValue) => ({
			...prevContextValue,
			activatedSensorMaintenanceMode: newValue,
			activatedMmsMode: false,
			activatedDigitalTwinMode: false,
			activatedInflowMode: false,
			activatedPastOverflowMode: false,
			activatedAlertMode: false,
			activatedSewerAnalysisMode: false,
		}));
		closeAndPublishMode(newValue ? 'sensor-maintenance' : 'default');
	};

	const toggleAlertMode = () => {
		const newValue = !contextValue.activatedAlertMode;
		setSelectedMode(newValue ? 'alerts' : 'default');

		if (!newValue) {
			publishDashboardMapEvent({ type: 'closePopup' });
		}

		setContextValue((prevContextValue) => ({
			...prevContextValue,
			activatedAlertMode: newValue,
			activatedDigitalTwinMode: false,
			activatedInflowMode: false,
			activatedMmsMode: false,
			activatedPastOverflowMode: false,
			activatedSensorMaintenanceMode: false,
			activatedSewerAnalysisMode: false,
		}));
		closeAndPublishMode(newValue ? 'alerts' : 'default');
	};

	const selectSewerAnalysisMode = () => {
		setSelectedMode('sewer-analysis');
		publishDashboardMapEvent({ type: 'closePopup' });

		setContextValue((prevContextValue) => ({
			...prevContextValue,
			activatedMmsMode: false,
			activatedDigitalTwinMode: false,
			activatedInflowMode: false,
			activatedSewerAnalysisMode: true,
			activatedPastOverflowMode: false,
			activatedSensorMaintenanceMode: false,
			activatedAlertMode: false,
		}));
		onOpenChange(false);
		publishDashboardMapEvent({ type: 'setMode', data: 'sewer-analysis' });
		openModalKeplerAnalysisHistorial();
	};

	const hasModule = (name: string, id: number) =>
		modules?.some((m) => m?.name === name && m?.id === id);

	return (
		<div className="flex items-center h-8">
			<DropdownMenu open={open} onOpenChange={onOpenChange}>
				<DropdownMenu.Trigger>
					<div
						className="flex items-center justify-between h-8"
						style={{
							width: isSmallScreen ? 38 : 150,
							border: '1px solid #75798f',
							borderRadius: 20,
							padding: isSmallScreen ? '0 6px' : '0 8px',
							backgroundColor: 'white',
							color: '#434B56',
							justifyContent: isSmallScreen ? 'center' : undefined,
						}}
					>
						<span
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								marginRight: isSmallScreen ? 0 : 8,
							}}
						>
							{MODE_ICONS[selectedMode]}
						</span>
						{!isSmallScreen && <span style={{ fontSize: 12 }}>{selectedModeLabel}</span>}
						{!isSmallScreen && <ChevronDownIcon />}
					</div>
				</DropdownMenu.Trigger>
				<DropdownMenu.Content width={290}>
					<DropdownMenu.IconGrid columns={3}>
						<DropdownMenu.IconItem
							icon={<MapIcon />}
							label="Default Map"
							selected={selectedMode === 'default'}
							onSelect={selectDefaultMode}
						/>
						{FUTURE_FEATURES.alertsMode && (
							<DropdownMenu.IconItem
								icon={<Icon name="exclamation-triangle" />}
								label="Alerts"
								selected={selectedMode === 'alerts'}
								onSelect={toggleAlertMode}
							/>
						)}
						<DropdownMenu.IconItem
							icon={<Icon name="virtual-twin" />}
							label="Digital Twin Lens"
							selected={selectedMode === 'digital-twin'}
							onSelect={toggleDigitalTwinMode}
						/>
						{hasModule('DMA', 7) && (
							<DropdownMenu.IconItem
								icon={<Icon name="file-graph" />}
								label="DMA Lens"
								selected={selectedMode === 'dma'}
								onSelect={selectDmaMode}
							/>
						)}
						{(isWaterNetwork || isDrainageNetwork) && (
							<DropdownMenu.IconItem
								icon={<Icon name="chart-pie-alt" />}
								label={analysisModeLabel}
								selected={selectedMode === 'sewer-analysis'}
								onSelect={selectSewerAnalysisMode}
							/>
						)}
						<DropdownMenu.IconItem
							icon={<InflowSvg />}
							label="Wet Weather Stress"
							selected={selectedMode === 'inflow'}
							onSelect={toggleInflowMode}
						/>
						{hasModule('MMS', 9) && (
							<DropdownMenu.IconItem
								icon={<Icon name="code-branch" />}
								label="Hydraulic Model Workbench"
								selected={selectedMode === 'mms'}
								onSelect={toggleMmsMode}
							/>
						)}
						<DropdownMenu.IconItem
							icon={<Icon name="processor" />}
							label="Sensor Maintenance"
							selected={selectedMode === 'sensor-maintenance'}
							onSelect={toggleSensorMaintenanceMode}
						/>
						{FUTURE_FEATURES.pastOverflowMode && (
							<DropdownMenu.IconItem
								icon={<Icon name="history" />}
								label="Past Overflow"
								selected={selectedMode === 'past-overflow'}
								onSelect={togglePastOverflowMode}
							/>
						)}
						{FUTURE_FEATURES.heatmapMode && (
							<DropdownMenu.IconItem
								icon={<ExperimentOutlined style={{ fontSize: 20 }} rev={undefined} />}
								label="Heatmap POC"
								selected={selectedMode === 'heatmap'}
								onSelect={toggleHeatmapMode}
							/>
						)}
					</DropdownMenu.IconGrid>
				</DropdownMenu.Content>
			</DropdownMenu>
		</div>
	);
};

export default MapModeMenu;
